import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, contentChild, inject, input, TemplateRef } from "@angular/core";
import { BaseComponent } from "primeng/basecomponent";
import { RowdetailsStyle } from "./rowdetails.style";

/**
 * Shows additional data for table rows in a canonical way.
 * Mostly used in expanded row templates of tables.
 *
 * @example
 * ```html
 * <p-table>
 *   <ng-template #expandedrow let-data>
 *     <ui-rowdetails>
 *       <ng-template #body>
 *         <tr>
 *           <td>{{ t("name.title") }}</td>
 *           <td>{{ data.name }}</td>
 *         </tr>
 *       </ng-template>
 *     </ui-rowdetails>
 *   </ng-template>
 * </p-table>
 * ```
 */
@Component({
  selector: "ui-rowdetails",
  templateUrl: "./rowdetails.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  host: { "[class]": "cx('root')" },
})
export class RowdetailsComponent extends BaseComponent {
  protected readonly _componentStyle = inject(RowdetailsStyle);

  /**
   * If the component is used in a fixed layout, e.g. a `p-treetable`,
   * the colspan attribute has to be provided so that the layout does not get broken.
   */
  readonly colspan = input<number>(1000);

  /**
   * Template tagged as `body` containing table rows with the additional data.
   *
   * @example
   * ```html
   * <ng-template #body>
   *   <tr>
   *     <td>{{ t("name.title") }}</td>
   *     <td>{{ data.name }}</td>
   *   </tr>
   * </ng-template>
   * ```
   */
  readonly body = contentChild.required<TemplateRef<void>>("body");
}
